import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { getAccountsStrings } from '../../accountStrings/accountStrings';
import { translateFn } from '../../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { AccountActionContext } from '../../types/AccountActionContext';
import _ from 'lodash';
import { SelectOption } from '../../../../ui/gzDropdown/types';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { AccountSource, AccountSourceId } from '../../types/AccountSources';
import { CompanyData } from '../../types/CompanyData';
import { AccountLoginSecurityData } from '../types';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { LinkType } from 'apps/webcomponents/src/ui/gzLinkComponent/types';

export class AccountLoginSecurityWebView extends BaseWebComponentView {
  public context: AccountActionContext;

  public formGroup: FormGroup;
  public dom: {
    $loginSecuritySection: HTMLElement;
    $loginSecuritySectionTitle: HTMLElement;
    $authenticationMethodDropdown: GzDropdownWebComponent;
    $authenticationMethodFormField: GzFormFieldWebComponent;
    $passwordExpirationFormField: GzFormFieldWebComponent;
    $passwordExpirationCheckbox: GzCheckboxWebComponent;
    $passwordExpirationDescription: GzTextWebComponent;
    $accountLockoutFormField: GzFormFieldWebComponent;
    $accountLockoutCheckbox: GzCheckboxWebComponent;
    $accountLockoutDescription: GzTextWebComponent;
    $concurrentSessionsFormField: GzFormFieldWebComponent;
    $concurrentSessionsCheckbox: GzCheckboxWebComponent;
    $concurrentSessionsDescription: GzTextWebComponent;
    $checkStatusDescription: GzTextWebComponent;
    $2faSection: HTMLElement;
    $2faStatusSectionDescription: GzTextWebComponent;
    $2faInfo: GzTextWebComponent;
    $2faEnableSteps: HTMLElement;
    $2faEnableStep1: GzTextWebComponent;
    $2faEnableStep2: GzTextWebComponent;
    $2faEnableStep3: GzTextWebComponent;
    $2faEnableStep4: GzTextWebComponent;
    $reset2faDescription: GzTextWebComponent;
    $passwordFormField: GzFormFieldWebComponent;
    $revokeResetDescription: GzTextWebComponent;
    $revokeButton: GzButtonWebComponent;
    $resetButton: GzButtonWebComponent;
    $afterResetDescription: GzTextWebComponent;
  };

  public userId: string;
  public twoFactorAuthentication: boolean;

  getDomElements(): void {
    this.dom = {
      $loginSecuritySection: this.shadow.querySelector('#login-security-section'),
      $loginSecuritySectionTitle: this.shadow.querySelector('#login-security-section-title'),
      $authenticationMethodFormField: this.shadow.querySelector('#authentication-method'),
      $authenticationMethodDropdown: this.shadow.querySelector('#authentication-method-dropdown'),
      $passwordExpirationFormField: this.shadow.querySelector('#password-expiration'),
      $passwordExpirationCheckbox: this.shadow.querySelector('#password-expiration-checkbox'),
      $passwordExpirationDescription: this.shadow.querySelector('#password-expiration-description'),
      $accountLockoutFormField: this.shadow.querySelector('#account-lockout'),
      $accountLockoutCheckbox: this.shadow.querySelector('#account-lockout-checkbox'),
      $accountLockoutDescription: this.shadow.querySelector('#account-lockout-description'),
      $concurrentSessionsFormField: this.shadow.querySelector('#concurrent-sessions'),
      $concurrentSessionsCheckbox: this.shadow.querySelector('#concurrent-sessions-checkbox'),
      $concurrentSessionsDescription: this.shadow.querySelector('#concurrent-sessions-description'),
      $checkStatusDescription: this.shadow.querySelector('#check-status-description'),
      $2faSection: this.shadow.querySelector('#two-fa-section'),
      $2faStatusSectionDescription: this.shadow.querySelector('#two-fa-status-description'),
      $2faInfo: this.shadow.querySelector('#two-fa-info'),
      $2faEnableSteps: this.shadow.querySelector('#two-fa-enable-steps'),
      $2faEnableStep1: this.shadow.querySelector('#two-fa-enable-step-1'),
      $2faEnableStep2: this.shadow.querySelector('#two-fa-enable-step-2'),
      $2faEnableStep3: this.shadow.querySelector('#two-fa-enable-step-3'),
      $2faEnableStep4: this.shadow.querySelector('#two-fa-enable-step-4'),
      $reset2faDescription: this.shadow.querySelector('#reset-2fa-description'),
      $passwordFormField: this.shadow.querySelector('#user-password'),
      $revokeResetDescription: this.shadow.querySelector('#revoke-reset-description'),
      $resetButton: this.shadow.querySelector('#reset-button'),
      $revokeButton: this.shadow.querySelector('#revoke-button'),
      $afterResetDescription: this.shadow.querySelector('#after-reset-description'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.translations = getAccountsStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$loginSecuritySectionTitle.innerText = this.translations.loginSecurityTitle;
    this.dom.$authenticationMethodFormField.label = this.translations.authenticationMethodLabel;
    this.dom.$passwordExpirationCheckbox.label = this.translations.passwordExpirationCheckboxLabel;
    this.dom.$passwordExpirationDescription.innerText = this.translations.passwordExpirationDescription;
    this.dom.$accountLockoutCheckbox.label = this.translations.accountLockoutCheckboxLabel;
    this.dom.$accountLockoutDescription.innerText = this.translations.accountLockoutDescription;
    this.dom.$concurrentSessionsCheckbox.label = this.translations.concurrentSessionsCheckboxLabel;
    this.dom.$concurrentSessionsDescription.innerText = this.translations.concurrentSessionsDescription;
    this.dom.$2faInfo.innerText = this.translations.twoFAInfoString;
    this.dom.$2faEnableStep1.innerText = this.translations.firstStepToEnable2faString;
    this.dom.$2faEnableStep2.innerText = this.translations.secondStepToEnable2faString;
    let kbLink =
      'https://www.bitdefender.com/business/support/en/77212-358013-onboarding.html#UUID-09ff5fef-e720-9ab8-fd28-0ffca7fe6647_section-idm973383117896756';
    if (this.dependencies.isCloud()) {
      kbLink =
        'https://www.bitdefender.com/business/support/en/77209-358013-onboarding.html#UUID-09ff5fef-e720-9ab8-fd28-0ffca7fe6647_section-idm4519428735809632992137064869';
      if (this.dependencies.isPartner()) {
        kbLink =
          'https://www.bitdefender.com/business/support/en/77211-358013-onboarding.html#UUID-09ff5fef-e720-9ab8-fd28-0ffca7fe6647_section-idm4519428735809632992137064869';
      }
    }

    this.dom.$2faEnableStep3.innerHTML =
      this.translations.thirdStepToEnable2faString +
      `<gz-link
        type="${LinkType.UNDERLINE}"
        href="${kbLink}"
        ><span>${translateFn('Learn more.')}</span></gz-link
      >`;
    this.dom.$2faEnableStep4.innerText = this.translations.fourthStepToEnable2faString;
    this.dom.$reset2faDescription.innerText = this.translations.reset2faString;
    this.dom.$passwordFormField.label = this.translations.gravityZonePasswordLabel;
    this.dom.$revokeResetDescription.innerText = this.translations.revokeResetDescriptionString;
    this.dom.$resetButton.innerText = this.translations.resetButtonLabel;
    this.dom.$revokeButton.innerText = this.translations.revokeButtonLabel;
    this.dom.$afterResetDescription.innerText = this.translations.afterResetString;
  }

  disconnected() {}

  buildSuccessView(viewData: AccountLoginSecurityData): void {
    this.setInitialValues();
    this.populateAuthenticationMethodDropdown(viewData?.companyInfo);
    this.showFieldsDependingOnPlatform(viewData);
    this.createFormGroup();
    this.resetData();

    if (this.context === AccountActionContext.Edit) {
      this.setFormFieldsValues(viewData);
    }
  }

  private setFormFieldsValues(viewData: AccountLoginSecurityData): void {
    this.userId = _.get(viewData, ['_id'], '');
    this.dom.$passwordExpirationFormField.value = _.get(viewData, ['passwordLifetime'], false);
    this.dom.$accountLockoutFormField.value = _.get(viewData, ['accountLockdown', 'enabled'], false);
    this.dom.$concurrentSessionsFormField.value = _.get(viewData, ['concurrentSessions', 'enabled'], true);

    this.twoFactorAuthentication = _.get(viewData, ['twoFactorAuthentication', 'enabled'], false);
    this.showFieldsDependingOnTwoFA();

    this.selectAuthenticationMethod(_.get(viewData, ['source'], AccountSource.MANUAL));
  }

  private showFieldsDependingOnPlatform(viewData: AccountLoginSecurityData): void {
    // onprem && create
    if (this.context === AccountActionContext.Create && !this.dependencies.isCloud()) {
      toggleVisibility(this.dom.$loginSecuritySection, false);
      toggleVisibility(this.dom.$passwordExpirationFormField, false);
      toggleVisibility(this.dom.$accountLockoutFormField, false);
      return;
    }

    // onprem && edit
    if (this.context === AccountActionContext.Edit && !this.dependencies.isCloud()) {
      toggleVisibility(this.dom.$2faSection, true);
      toggleVisibility(this.dom.$authenticationMethodFormField, false);
      toggleVisibility(this.dom.$passwordExpirationFormField, false);
      toggleVisibility(this.dom.$accountLockoutFormField, false);
      return;
    }

    // cloud && create
    if (this.context === AccountActionContext.Create && this.dependencies.isCloud()) {
      toggleVisibility(this.dom.$authenticationMethodFormField, true);
      toggleVisibility(this.dom.$2faSection, false);
      toggleVisibility(this.dom.$passwordExpirationFormField, true);
      toggleVisibility(this.dom.$accountLockoutFormField, true);
      return;
    }

    // cloud && edit
    if (this.context === AccountActionContext.Edit && this.dependencies.isCloud()) {
      toggleVisibility(this.dom.$authenticationMethodFormField, true);
      toggleVisibility(this.dom.$2faSection, true);
      toggleVisibility(this.dom.$passwordExpirationFormField, true);
      toggleVisibility(this.dom.$accountLockoutFormField, true);
      return;
    }
  }

  public showFieldsDependingOnTwoFA(): void {
    if (this.twoFactorAuthentication) {
      toggleVisibility(this.dom.$2faEnableSteps, false);
      this.dom.$checkStatusDescription.innerHTML = `
      <p>
        ${this.translations.checkStatusOfThePoliciesTwoFAString}
        <br>
        ${this.translations.readOnlyString}
        <br>
        <br>
        ${this.translations.twoFAEnabledStatusString}
      </p>`;
      return;
    }

    toggleVisibility(this.dom.$2faEnableSteps, true);
    toggleVisibility(this.dom.$reset2faDescription, false);
    toggleVisibility(this.dom.$passwordFormField, false);
    toggleVisibility(this.dom.$revokeResetDescription, false);
    toggleVisibility(this.dom.$revokeButton, false);
    toggleVisibility(this.dom.$resetButton, false);
    toggleVisibility(this.dom.$afterResetDescription, false);
    this.dom.$checkStatusDescription.innerHTML = `
      <p>
        ${this.translations.checkStatusOfThePoliciesTwoFAString}
        <br>
        ${this.translations.readOnlyString}
        <br>
        <br>
        ${this.translations.twoFADisabledStatusString}
      </p>`;
  }

  public resetData(): void {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.applyToFormGroupFields(field => {
      if (field instanceof GzFormFieldWebComponent) {
        field.discardChanges();
        field.resetField();
      }
    });
  }

  private createFormGroup(): void {
    this.formGroup = new FormGroup(this.getFormGroupItems());
    this.setFormFieldsValidators();
  }

  private getFormGroupItems(): {} {
    const formGroupItems: {} = {};

    if (this.dependencies.isCloud()) {
      formGroupItems['passwordExpiration'] = this.dom.$passwordExpirationFormField;
      formGroupItems['accountLockout'] = this.dom.$accountLockoutFormField;
      formGroupItems['authenticationMethod'] = this.dom.$authenticationMethodFormField;
    }

    formGroupItems['concurrentSessions'] = this.dom.$concurrentSessionsFormField;

    return formGroupItems;
  }

  private setFormFieldsValidators(): void {
    this.dom.$passwordExpirationFormField.Validators = [];
    this.dom.$accountLockoutFormField.Validators = [];
    this.dom.$concurrentSessionsFormField.Validators = [];
    this.dom.$authenticationMethodFormField.Validators = [];
  }

  private setInitialValues(): void {
    this.dom.$passwordExpirationFormField.initialValue = true;
    this.dom.$accountLockoutFormField.initialValue = true;
    this.dom.$concurrentSessionsFormField.initialValue = true;
  }

  private populateAuthenticationMethodDropdown(companyData: CompanyData): void {
    const options = [
      {
        title: translateFn('Login using GravityZone credentials'),
        value: AccountSource.MANUAL,
      },
      {
        title: translateFn('Login using GravityZone Identity Provider'),
        value: AccountSource.NEXUS,
      },
    ];

    const identityProvider = _.get(companyData, ['specifics', 'identityProvider', 'specifics', 'metadata'], false);
    if (identityProvider) {
      options.push({
        title: translateFn('Login using your Identity Provider'),
        value: AccountSource.SSO_SAML,
      });
    }

    this.dom.$authenticationMethodDropdown.setAttribute('options', JSON.stringify(options));
    this.dom.$authenticationMethodDropdown.selectedOption = options[identityProvider ? 2 : 0];
    this.dom.$authenticationMethodDropdown.emitOptionSelectedEvent();
  }

  private selectAuthenticationMethod(authenticationMethodValue: AccountSourceId): void {
    // if the source is VIFOR preselect MANUAL
    if (authenticationMethodValue === AccountSource.VIFOR) {
      this.dom.$authenticationMethodDropdown.selectedOption = this.dom.$authenticationMethodDropdown.options.find(
        option => option.value === AccountSource.MANUAL
      ) as SelectOption;
    } else {
      this.dom.$authenticationMethodDropdown.selectedOption = this.dom.$authenticationMethodDropdown.options.find(
        option => option.value === authenticationMethodValue
      ) as SelectOption;
    }
    this.dom.$authenticationMethodDropdown.emitOptionSelectedEvent();
  }

  public changeFormFieldsVisibilityDependingOnAuthenticationMethod(
    selectedAuthenticationMethod: AccountSourceId
  ): void {
    if (selectedAuthenticationMethod === AccountSource.SSO_SAML) {
      toggleVisibility(this.dom.$2faSection, false);
      toggleVisibility(this.dom.$passwordExpirationFormField, false);
      toggleVisibility(this.dom.$accountLockoutFormField, false);
      return;
    }

    if (this.context === AccountActionContext.Edit) {
      toggleVisibility(this.dom.$2faSection, true);
    }

    if (this.dependencies.isCloud()) {
      toggleVisibility(this.dom.$passwordExpirationFormField, true);
      toggleVisibility(this.dom.$accountLockoutFormField, true);
    }
  }

  public updateSettingsDependingOnCompany(company: CompanyData): void {
    this.populateAuthenticationMethodDropdown(company);

    const authenticationMethodFormVisible = this.dependencies.isCloud();

    toggleVisibility(this.dom.$authenticationMethodFormField, authenticationMethodFormVisible);

    const userHasGzSource = [AccountSource.MANUAL, AccountSource.NEXUS].includes(this.dom.$authenticationMethodDropdown.selectedOption?.value as AccountSource);

    const accountLoginSettingVisible = authenticationMethodFormVisible && userHasGzSource;

    toggleVisibility(this.dom.$passwordExpirationFormField, accountLoginSettingVisible);
    toggleVisibility(this.dom.$accountLockoutFormField, accountLoginSettingVisible);
  }

  public showNoPasswordSpecifiedMessage(windowTitle: string, windowBody: string): void {
    const windowBodyHtml = `
      <p>
      ${windowBody}
      </p>`;

    const windowFooterHtml = `<gz-button id='cancel' type='outline' color='primary' class='mr-2'>
       ${translateFn('Ok')}
      </gz-button>`;

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, null, null, false);
  }
}
